import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
// Components
import Filter from './Filter';
import ApplyButton from 'shared/buttons/ApplyButton';
import { Label } from '@progress/kendo-react-labels';
import Input from 'shared/inputs/BaseInput';
import { useToast } from 'shared/toast';
import { FormDropDownList } from 'shared/formControls';
import { SaveButton, ResetButton } from 'shared/buttons';
import UserFilterList from './UserFilterList';
import ConfirmDialogue, { showConfirmDialogue } from 'shared/modals/ConfirmDialogue';
// Fetch
import {
  addFilter as addFilterRequest,
  updateFilter as updateFilterRequest,
  deleteFilter as deleteFilterRequets,
  getFiltersForCurrentUser,
  getFilterFieldsForEntity,
} from 'src/fetch/filters';
// Consts
import { initialFilter as defaultFilter } from './constants';
// Styles
import './style.scss';
import { theme } from 'src/theme';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const FilteringPanel = ({
  entity,
  onApply,
  initialFilter,
  // API
  getFilters = getFiltersForCurrentUser,
  addFilter = addFilterRequest,
  updateFilter = updateFilterRequest,
  deleteFilter = deleteFilterRequets,
  getFilterFields = getFilterFieldsForEntity,
}) => {
  if (!entity || (Array.isArray(entity) && entity.length === 0)) {
    throw new Error('Filtering Entity is not specified.');
  }

  const toast = useToast();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState(initialFilter ?? defaultFilter);
  const userFiltersListRef = useRef();
  const [showToast, setShowToast] = useState(false);

  const [currentEntity, setCurrentEntity] = useState(Array.isArray(entity) ? entity[0]?.value : entity);

  const [isResetConfirmShowing, setIsResetConfirmShowing] = useState(false);
  const [existingFilterModalShowing, setExistingFilterModalShowing] = useState(false);

  useEffect(() => {
    setFilter(initialFilter ?? defaultFilter);
    if (initialFilter?.id) {
      userFiltersListRef.current.selectFilter(initialFilter.id);
    }
  }, [initialFilter]);

  const handleFilterSelected = (filter) => {
    setFilter(filter);
  };

  const onConfirmClose = (didConfirm) => {
    if (didConfirm) {
      setFilter(defaultFilter);
    }
    setIsResetConfirmShowing(false);
  };

  const onExistingConfirmClose = async (didConfirm) => {
    if (didConfirm) {
      await handleUpdateExistingFilter();
    }
    setExistingFilterModalShowing(false);
  };

  const hasMultipleEntities = () => Array.isArray(entity) && entity.length > 1;

  const handleNameChange = ({ value }) => {
    setFilter((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const handleNew = () => {
    setFilter({
      ...defaultFilter,
      expression: {
        ...defaultFilter.expression,
      },
    });
    setFilter(defaultFilter);
    userFiltersListRef.current.resetSelection();
  };

  const handleFilterChange = (expression) => {
    const addValueToFilters = (filters) => filters.map((filter) => {
      if (filter.filters) {
        return { ...filter, filters: addValueToFilters(filter.filters) };
      } if (filter.operator === 'isempty' || filter.operator === 'isnotempty') {
        return {
          ...filter,
          value: '',
          fieldInfo: {
            name: filter.field,
            nestedFileInfo: null,
            metadata: { collection: false },
          },
        };
      }
      return filter;
    });

    const updatedExpression = {
      ...expression,
      filters: addValueToFilters(expression.filters),
    };

    // Reset showToast if all filters have values
    if (updatedExpression.filters.some((filter) => filter.value !== '')) {
      setShowToast(false);
    }

    setFilter((prev) => ({
      ...prev,
      changed: true,
      expression: updatedExpression,
    }));
  };

  const handleUpdateExistingFilter = async () => {
    const existingFilter = userFiltersListRef.current.getFilterByName(filter.name);
    const savePromise = updateFilter(existingFilter.id, { name: filter.name, expression: filter.expression }, currentEntity);

    try {
      await savePromise;
      await userFiltersListRef.current.reload();
      toast.success({ message: 'Filter saved successfully.' });
      if (filter) {
        setFilter((prev) => ({ ...prev, changed: false }));
      }
      handleNew();
    } catch (error) {
      toast.error({ message: 'Failed to save filter', error });
    }
  };

  const handleSave = async () => {
    if (existingFilterModalShowing) {
      return;
    }

    const existingFilter = userFiltersListRef.current.getFilterByName(filter.name);

    if (existingFilter) {
      setExistingFilterModalShowing(true);
    } else {
      const savePromise = !filter.id
        ? () => addFilter(filter, currentEntity)
        : () => updateFilter(filter.id, { name: filter.name, expression: filter.expression }, currentEntity);

      try {
        await savePromise();
        await userFiltersListRef.current.reload();
        toast.success({ message: 'Filter saved successfully.' });
        if (filter) {
          setFilter((prev) => ({ ...prev, changed: false }));
        }
      } catch (error) {
        toast.error({ message: 'Failed to save filter', error });
      }
    }
  };

  const handleDeleteFilter = async ({ id, name }, selectedFilterId) => {
    try {
      await deleteFilter(id, currentEntity);
      await userFiltersListRef.current.reload();

      if (id === selectedFilterId) {
        handleNew();
        userFiltersListRef.current.resetSelection();
      }

      toast.success({ message: 'Filter deleted successfully.' });
    } catch (error) {
      toast.error({ message: 'Failed to delete filter', error });
    }
  };

  const handleSelectedItemDeleted = () => handleNew();

  const handleReset = () => setIsResetConfirmShowing(true);

  const validateBetweenExpression = (expr) => {
    if (!expr.value) {
      return false;
    }

    const { value: { start, end } } = expr;

    if (!start && !end) {
      return false;
    }

    if (start instanceof Date && end instanceof Date) {
      return start.getTime() <= end.getTime();
    }

    return true;
  };

  const handleShowEmptyValueErrorToast = () => {
    if (!showToast) {
      toast.warning({ message: 'Value cannot be empty. Use "is empty" or "is not empty" operators instead.' }, () => {
      });
      setShowToast(true);
    }
  };

  const validateFilter = (expr) => {
    if ('operator' in expr) {
      if (expr.operator === 'between') {
        return validateBetweenExpression(expr);
      }

      if (expr.operator === 'isempty' || expr.operator === 'isnotempty' || expr.operator === 'isnull' || expr.operator === 'isnotnull') {
        return true;
      }

      if (expr.value === '' && expr.value !== null) {
        expr.value = null;
        handleShowEmptyValueErrorToast();
        return false;
      }

      return expr?.value != null;
    }

    const filters = expr?.filters || [];
    const childrenHaveNoEmptyLogicalNodes = filters.map((x) => validateFilter(x)).reduce((x, y) => x && y, true);
    return filters.length > 0 && childrenHaveNoEmptyLogicalNodes;
  };

  const handleEntityChanged = ({ value: { value } }) => {
    handleNew();
    setCurrentEntity(value);
  };

  const getEntityObject = () => entity.find(({ value }) => value === currentEntity);

  return (
    <div className='filtering-panel'>
      {hasMultipleEntities() && (
        <div style={{ margin: 0, marginBottom: '1rem' }}>
          <FormDropDownList
            label='Entity'
            id='entity'
            valueField='value'
            textField='title'
            value={getEntityObject()}
            data={entity}
            onChange={handleEntityChanged}
          />
        </div>
      )}
      <div className='filtering-panel-content'>
        <div style={{ width: '20%' }}>
          <UserFilterList
            entity={currentEntity}
            onSelected={handleFilterSelected}
            onDelete={handleDeleteFilter}
            onSelectedItemDeleted={handleSelectedItemDeleted}
            getFilters={getFilters}
            ref={userFiltersListRef}
            onAdd={handleNew}
          />
        </div>
        <div
          style={{
            width: '96%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            marginLeft: '2rem',
          }}
        >
          <div style={{ display: 'flex' }}>
            <div style={{ flex: '1 1 auto' }} />
          </div>
          <div style={{ margin: '0 0 0.5rem 0' }}>
            <Label>Filter Name</Label>
            <Input value={filter.name} onChange={handleNameChange} id='filterName' />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', marginTop: '0.5rem' }}>
            <Filter
              entity={currentEntity}
              value={filter.expression}
              onChange={handleFilterChange}
              getFilterFields={getFilterFields}
            />
          </div>
        </div>
      </div>
      <div>
        <ApplyButton
          icon='filter'
          onClick={() => onApply({
            entity: currentEntity,
            filter,
          })}
          disabled={!validateFilter(filter.expression)}
        />
        <SaveButton
          onClick={handleSave}
          disabled={!validateFilter(filter.expression)}
          style={{ marginLeft: '0.5rem' }}
        />
        <ResetButton
          onClick={handleReset}
          disabled={filter.expression?.filters?.length === 0}
          style={{ position: 'absolute', left: '92%' }}
        />
      </div>
      <ConfirmDialogue
        handleClose={onConfirmClose}
        headerText='Reset filter'
        text='Are you sure you want to reset this filter?'
        isOpen={isResetConfirmShowing}
      />
      <ConfirmDialogue
        handleClose={onExistingConfirmClose}
        headerText='Existing Filter'
        text={`A filter with the name "${filter.name}" already exists. Do you want to overwrite it?`}
        isOpen={existingFilterModalShowing}
      />
    </div>
  );
};

export default FilteringPanel;
